import React from 'react';
import { tr } from '../../i18n';
import './TableSkeleton.css';

export type TableSkeletonProps = {
  /** Number of skeleton rows. Default: 8 */
  rows?: number;
  /**
   * Column widths. Either a number (equal flex:1 columns) or an array of CSS
   * width strings, e.g. ['28%', '14%', '14%', '14%', '14%', '16%'].
   */
  columns: number | string[];
  /** Render a skeleton header row. Default: true */
  showHeader?: boolean;
  /**
   * When true, the first column in each body row is rendered as a circle
   * avatar (32×32, var(--control-h-sm)) with a line beside it.
   * Default: false
   */
  withAvatar?: boolean;
  /** Additional class on the root element */
  className?: string;
  /** aria-label for the status container. Default: 'Загрузка данных' */
  ariaLabel?: string;
};

function resolveWidths(columns: number | string[]): string[] {
  if (typeof columns === 'number') {
    return Array.from({ length: columns }, () => '');
  }
  return columns;
}

export function TableSkeleton({
  rows = 8,
  columns,
  showHeader = true,
  withAvatar = false,
  className,
  ariaLabel = tr.shell.tableSkeleton.ariaLabel,
}: TableSkeletonProps): React.ReactElement {
  const widths = resolveWidths(columns);
  const equalFlex = typeof columns === 'number';

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      className={`tskel-root${className ? ` ${className}` : ''}`}
    >
      {/* header */}
      {showHeader && (
        <div className="tskel-header">
          {widths.map((w, i) => (
            <div
              key={i}
              className="tskel-header-cell"
              style={equalFlex ? { flex: 1 } : { width: w }}
            />
          ))}
        </div>
      )}

      {/* body rows */}
      {Array.from({ length: rows }, (_, rowIdx) => (
        <div key={rowIdx} className="tskel-row">
          {widths.map((w, colIdx) => {
            const isFirstCol = colIdx === 0;
            if (withAvatar && isFirstCol) {
              return (
                <div
                  key={colIdx}
                  className="tskel-avatar-cell"
                  style={equalFlex ? { flex: 1 } : { width: w }}
                >
                  <div className="tskel-avatar" />
                  <div className="tskel-avatar-line" />
                </div>
              );
            }
            return (
              <div
                key={colIdx}
                className="tskel-cell"
                style={equalFlex ? { flex: 1 } : { width: w }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default TableSkeleton;
